import React, { useState, useEffect, useRef, useCallback } from "react";
import myAxios from "../../_services/axiosilearnWrapper";
import Config from "../../_config/config";
import "./WhiteboardOverlay.css";

const POLL_INTERVAL_MS = 2000;
const BACKGROUND = "var(--surface-card)";

/**
 * Student-side live whiteboard overlay.
 *
 * Polls /v1/flip-class/whiteboard/active with sinceSeq for delta strokes,
 * applies them to a read-only canvas. Mirrors the FlashQuizOverlay polling
 * pattern.
 *
 * Event types from the server:
 *   stroke / highlight → render via drawStroke
 *   clear              → wipe canvas
 *   undo               → drop last stroke and redraw
 *
 * `startMinimized` controls the initial presentation when a board goes live:
 * inside a flip class (default false) it opens as the full-screen board the
 * student came to attend; mounted app-wide outside flip class (true) it opens
 * as a small, non-intrusive pill the student taps to view, so a live board
 * never hijacks whatever page they're on.
 */
const WhiteboardOverlay = ({ batchId, enquiryId, startMinimized = false }) => {
  const [sessionId, setSessionId] = useState(null);
  const [active, setActive] = useState(false);
  const [minimized, setMinimized] = useState(startMinimized);

  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const strokesRef = useRef([]);
  const sinceSeqRef = useRef(0);
  const pollRef = useRef(null);
  const sessionIdRef = useRef(null);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  const resizeAndRedraw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(rect.height * dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    redrawAll();
  }, []);

  const redrawAll = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const stroke of strokesRef.current) {
      drawStroke(ctx, canvas.width, canvas.height, stroke);
    }
  };

  useEffect(() => {
    // Only size/redraw while the full board is on screen. When collapsed to the
    // pill the canvas is unmounted; strokes still accumulate in strokesRef and
    // are redrawn in full on expand.
    if (!active || minimized) return undefined;
    resizeAndRedraw();
    const onResize = () => resizeAndRedraw();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [active, minimized, resizeAndRedraw]);

  const applyEvent = (evt) => {
    if (evt.type === "clear") {
      strokesRef.current = [];
      redrawAll();
      return;
    }
    if (evt.type === "undo") {
      strokesRef.current.pop();
      redrawAll();
      return;
    }
    if (!evt.points || evt.points.length < 2) return;
    const stroke = {
      type: evt.type === "highlight" ? "highlight" : "stroke",
      color: evt.color || "#1f2937",
      width: evt.width || 4,
      points: evt.points,
    };
    strokesRef.current.push(stroke);
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) drawStroke(ctx, canvas.width, canvas.height, stroke);
  };

  const poll = useCallback(async () => {
    if (!batchId && !enquiryId) return;
    try {
      const params = { sinceSeq: sinceSeqRef.current };
      if (batchId) params.batchId = batchId;
      else if (enquiryId) params.enquiryId = enquiryId;

      const response = await myAxios.get(
        `${Config.nodeSiteUrl}v1/flip-class/whiteboard/active`,
        { params },
      );
      const data = response.data;

      if (!data?.success || !data?.data) {
        if (sessionIdRef.current) {
          // Session ended
          setSessionId(null);
          setActive(false);
          strokesRef.current = [];
          sinceSeqRef.current = 0;
        }
        return;
      }

      const payload = data.data;

      if (sessionIdRef.current !== payload.sessionId) {
        // New session — reset state and re-surface at the configured presentation
        // (pill when mounted app-wide, full board inside flip class).
        setSessionId(payload.sessionId);
        strokesRef.current = [];
        sinceSeqRef.current = 0;
        setMinimized(startMinimized);
      }
      setActive(true);

      const events = payload.events || [];
      for (const evt of events) {
        applyEvent(evt);
        if (evt.seq > sinceSeqRef.current) sinceSeqRef.current = evt.seq;
      }

      if (payload.status === "closed") {
        setSessionId(null);
        setActive(false);
        strokesRef.current = [];
        sinceSeqRef.current = 0;
      }
    } catch (err) {
      // Silently ignore poll errors — match FlashQuizOverlay behavior
      // eslint-disable-next-line no-console
      console.warn("Whiteboard poll error:", err.message);
    }
  }, [batchId, enquiryId, startMinimized]);

  useEffect(() => {
    if (!batchId && !enquiryId) return undefined;
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [batchId, enquiryId, poll]);

  if (!active) return null;

  if (minimized) {
    return (
      <button
        type="button"
        className="wb-student-pill"
        onClick={() => setMinimized(false)}
        aria-label="Open the live whiteboard"
      >
        <span className="wb-student-pill-dot" aria-hidden="true" />
        <span className="wb-student-pill-text">
          Live Whiteboard
          <span className="wb-student-pill-sub">Teacher is explaining · View</span>
        </span>
      </button>
    );
  }

  return (
    <div className="wb-student-overlay">
      <div className="wb-student-card">
        <div className="wb-student-header">
          <span className="wb-student-label">Live Whiteboard</span>
          <div className="wb-student-header-right">
            <span className="wb-student-hint">Teacher is explaining</span>
            <button
              type="button"
              className="wb-student-min-btn"
              onClick={() => setMinimized(true)}
              aria-label="Minimize whiteboard"
            >
              Minimize
            </button>
          </div>
        </div>
        <div ref={containerRef} className="wb-student-stage">
          <canvas ref={canvasRef} className="wb-student-canvas" style={{ background: BACKGROUND }} />
        </div>
      </div>
    </div>
  );
};

function drawStroke(ctx, w, h, stroke) {
  const pts = stroke.points;
  if (!pts || pts.length < 2) return;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(a.x * w, a.y * h);
    ctx.lineTo(b.x * w, b.y * h);
    ctx.strokeStyle = stroke.color;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (stroke.type === "highlight") {
      ctx.globalAlpha = 0.35;
      ctx.lineWidth = stroke.width * 3;
    } else {
      ctx.globalAlpha = 1;
      ctx.lineWidth = stroke.width;
    }
    ctx.stroke();
    ctx.restore();
  }
}

export default WhiteboardOverlay;
